import React from "react";
import { ArrowUpRight, Smartphone, Apple } from "lucide-react";
import { useI18n } from "../i18n-context";
import { LOGO_SQUARE } from "../assets";

// ---- Platform-specific store badges (official store guidelines) ----
function AppStoreBadge({ lang }) {
  return (
    <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-brand-ink text-white">
      <Apple className="h-7 w-7" />
      <div className="text-start leading-tight">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60">
          {lang === "ar" ? "حمّله من" : "Download on the"}
        </div>
        <div className="font-display font-bold text-base">App Store</div>
      </div>
    </div>
  );
}

function PlayStoreBadge({ lang }) {
  return (
    <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-brand-ink text-white">
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.609 1.814L13.792 12 3.61 22.186a1.5 1.5 0 01-.61-1.207V3.02a1.5 1.5 0 01.609-1.207z" fill="#34A853"/>
        <path d="M14.842 10.949l2.583-1.48L5.86 2.4a1.5 1.5 0 00-1.89.294L14.842 10.95z" fill="#EA4335"/>
        <path d="M14.842 13.051L3.97 23.306a1.5 1.5 0 001.89.294l11.565-7.07-2.583-1.479z" fill="#FBBC04"/>
        <path d="M20.16 10.44l-2.735-1.571-2.584 2.131 2.584 2.131 2.735-1.572a1.5 1.5 0 000-2.6z" fill="#4285F4"/>
      </svg>
      <div className="text-start leading-tight">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60">
          {lang === "ar" ? "حمّله من" : "GET IT ON"}
        </div>
        <div className="font-display font-bold text-base">Google Play</div>
      </div>
    </div>
  );
}

function AppCard({ app, lang }) {
  const isIOS = app.os === "iOS";
  return (
    <a
      href={app.link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={app.testId}
      className="group flex items-center gap-5 p-5 rounded-2xl bg-white border-2 border-brand-ink/10 hover:border-brand-blue hover:shadow-xl transition-all"
    >
      {/* Rectangular icon — app logo */}
      <div className="flex-shrink-0 h-20 w-20 md:h-24 md:w-24 rounded-2xl overflow-hidden bg-brand-paper border border-brand-ink/10 flex items-center justify-center">
        <img src={LOGO_SQUARE} alt="" className="h-full w-full object-cover" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] font-mono uppercase tracking-[0.18em] ${isIOS ? "text-brand-ink/60" : "text-green-700"}`}>
            {isIOS ? "iOS" : "Android"}
          </span>
          {app.badge && (
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand-orange">
              · {app.badge}
            </span>
          )}
        </div>
        <div className="font-display font-bold text-lg md:text-xl text-brand-ink leading-tight mb-1">
          {app.t}
        </div>
        <div className="text-xs md:text-sm text-brand-ink/60 leading-snug line-clamp-2">
          {app.d}
        </div>
      </div>

      <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-brand-ink/30 group-hover:text-brand-blue transition-colors" />
    </a>
  );
}

export default function AppsPage() {
  const { t, lang } = useI18n();

  // Split apps by platform
  const cards = t.apps.cards.map((c, i) => ({ ...c, testId: `app-card-${i}` }));
  const iosApps = cards.filter((c) => c.os === "iOS");
  const androidApps = cards.filter((c) => c.os === "Android");

  return (
    <>
      {/* CENTERED HEADLINE */}
      <section className="pt-32 pb-10 md:pt-40 md:pb-14 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-40" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[460px] w-[460px] rounded-full bg-brand-blue/10 blur-3xl pointer-events-none" />

        <div className="container relative">
          <div className="kicker justify-center mb-5" data-testid="apps-kicker">
            <span className="kicker-dot" />
            {t.apps.kicker}
          </div>
          <h1
            className={`headline text-brand-ink mb-5 leading-[0.95] ${
              lang === "ar" ? "text-4xl md:text-5xl" : "text-6xl md:text-7xl lg:text-8xl"
            }`}
            data-testid="apps-title"
          >
            {lang === "ar" ? "تطبيقات أكوان" : "Akwaan Applications"}
          </h1>
          <p className="text-lg md:text-xl text-brand-ink/65 leading-relaxed max-w-3xl mx-auto">
            {t.apps.lead}
          </p>
        </div>
      </section>

      {/* TWO-COLUMN APPS LAYOUT */}
      <section className="pb-24">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* LEFT — iOS */}
            <div data-testid="apps-ios-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-xl bg-brand-ink flex items-center justify-center">
                  <Apple className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-brand-ink/50">
                    {lang === "ar" ? "لأجهزة iPhone" : "For iPhone"}
                  </div>
                  <div className="font-display font-extrabold text-2xl md:text-3xl text-brand-ink leading-none">
                    {lang === "ar" ? "تحميل للأيفون" : "Download for iOS"}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {iosApps.map((app) => (
                  <AppCard key={app.link} app={app} lang={lang} />
                ))}
              </div>
            </div>

            {/* RIGHT — Android */}
            <div data-testid="apps-android-col">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-xl bg-[#1B5E20] flex items-center justify-center">
                  <Smartphone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-brand-ink/50">
                    {lang === "ar" ? "لأجهزة Android" : "For Android"}
                  </div>
                  <div className="font-display font-extrabold text-2xl md:text-3xl text-brand-ink leading-none">
                    {lang === "ar" ? "تحميل أندرويد" : "Download for Android"}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {androidApps.map((app) => (
                  <AppCard key={app.link} app={app} lang={lang} />
                ))}
              </div>
            </div>
          </div>

          {/* OS info footer */}
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border-2 border-brand-ink/10 p-7">
              <div className="flex items-center gap-3 mb-3">
                <Apple className="h-5 w-5 text-brand-blue" />
                <span className="font-display font-bold text-xl text-brand-ink">iOS</span>
              </div>
              <p className="text-sm text-brand-ink/60 leading-relaxed">
                {lang === "ar"
                  ? "تطبيق Akwaan Delivery للعملاء وتطبيق التاجر متاحان الآن على App Store. تطبيق المندوب لـ iOS قريباً."
                  : "Akwaan Delivery (customer) and the Merchant app are live on the App Store. The iOS Driver app is coming soon."}
              </p>
            </div>
            <div className="rounded-3xl border-2 border-brand-ink/10 p-7">
              <div className="flex items-center gap-3 mb-3">
                <Smartphone className="h-5 w-5 text-brand-blue" />
                <span className="font-display font-bold text-xl text-brand-ink">Android</span>
              </div>
              <p className="text-sm text-brand-ink/60 leading-relaxed">
                {lang === "ar"
                  ? "تطبيقات التاجر والمندوب متاحة الآن على Google Play. تأكد من تحميل التطبيق الصحيح حسب دورك."
                  : "Merchant and driver apps are live now on Google Play. Make sure to download the correct one for your role."}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
